import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Play,
  Square,
  Zap,
  Filter,
  ChevronRight,
  RefreshCw,
  Pencil,
  X,
  Save,
  Trash2,
  Check,
} from 'lucide-react';
import {
  StrategyConfig,
  STRATEGY_CATEGORIES,
  STRATEGY_FILTER_FIELDS,
  FILTER_OPERATORS,
  STRATEGY_COLORS,
  strategyApiService,
} from '@/services/strategyConfigService';
import { StrategyBuilder } from './StrategyBuilder';
import { StrategyPresetBrowser } from './StrategyPresetBrowser';
import signalRService from '@/services/signalRService';
import type { ScannerResult, ScannerUpdate } from '@/services/scannerService';

// ── Formatters ─────────────────────────────────────────────

const fmtPrice = (v: number) => (v < 1 ? v.toFixed(4) : v.toFixed(2));
const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
const fmtVol = (v: number) => {
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toString();
};
const fmtTime = (s: string) => {
  const d = new Date(s);
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'America/New_York',
  });
};
const chgColor = (v: number) =>
  v > 0
    ? 'text-green-600 dark:text-green-400'
    : v < 0
      ? 'text-red-600 dark:text-red-400'
      : 'text-muted-foreground';

// ── Active strategy tracking ───────────────────────────────

interface ActiveStrategy {
  config: StrategyConfig;
  results: ScannerResult[];
  totalMatches: number;
  enabled: boolean;
  color: string;
}

// ── Row with strategy info ─────────────────────────────────

interface ColoredRow {
  result: ScannerResult;
  strategyId: string;
  strategyName: string;
  color: string;
}

// ── Component ──────────────────────────────────────────────

export const StrategyDashboard: React.FC = () => {
  // ── View mode ──
  const [view, setView] = useState<'browse' | 'saved' | 'detail' | 'results'>('browse');

  // ── Builder dialog ──
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<StrategyConfig | null>(null);

  // ── Selection / detail ──
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyConfig | null>(null);

  // ── Saved (persisted) strategies ──
  const [savedStrategies, setSavedStrategies] = useState<StrategyConfig[]>([]);
  const [loadingSaved, setLoadingSaved] = useState(false);

  // ── Active strategies (running in panel — each color-coded) ──
  const [activeStrategies, setActiveStrategies] = useState<Map<string, ActiveStrategy>>(new Map());
  const activeRef = useRef(activeStrategies);
  activeRef.current = activeStrategies;

  // ── Next color index ──
  const colorIndexRef = useRef(0);
  const getNextColor = useCallback(() => {
    const c = STRATEGY_COLORS[colorIndexRef.current % STRATEGY_COLORS.length];
    colorIndexRef.current++;
    return c;
  }, []);

  // ── Load saved strategies from DB on mount ──
  useEffect(() => {
    loadSavedStrategies();
  }, []);

  const loadSavedStrategies = useCallback(async () => {
    setLoadingSaved(true);
    try {
      const list = await strategyApiService.getSaved();
      setSavedStrategies(list);
    } catch (err) {
      console.error('Failed to load saved strategies:', err);
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  // ── SignalR subscription to strategy updates ──
  useEffect(() => {
    const handler = (scannerId: string, update: ScannerUpdate) => {
      const stratId = scannerId.replace('strategy-', '');
      setActiveStrategies(prev => {
        const next = new Map(prev);
        const existing = next.get(stratId);
        if (existing) {
          next.set(stratId, {
            ...existing,
            results: update.results,
            totalMatches: update.totalMatchingSymbols,
          });
        }
        return next;
      });
    };

    signalRService.addScannerUpdateListener('strategy-dashboard', handler);
    return () => {
      signalRService.removeScannerUpdateListener('strategy-dashboard');
    };
  }, []);

  // ── Save a strategy to the database ──
  const saveStrategy = useCallback(async (config: StrategyConfig) => {
    try {
      const existing = savedStrategies.find(s => s.id === config.id);
      let saved: StrategyConfig;
      if (existing) {
        saved = await strategyApiService.update(config.id, config);
        setSavedStrategies(prev => prev.map(s => s.id === saved.id ? saved : s));
      } else {
        saved = await strategyApiService.create(config);
        setSavedStrategies(prev => [...prev, saved]);
      }
      return saved;
    } catch (err) {
      console.error('Failed to save strategy:', err);
      throw err;
    }
  }, [savedStrategies]);

  // ── Delete a saved strategy ──
  const deleteSavedStrategy = useCallback(async (id: string) => {
    try {
      await strategyApiService.deleteStrategy(id);
      setSavedStrategies(prev => prev.filter(s => s.id !== id));
      // Also disable if active
      if (activeStrategies.has(id)) {
        try { await strategyApiService.disable(id); } catch { /* ignore */ }
        setActiveStrategies(prev => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to delete strategy:', err);
    }
  }, [activeStrategies]);

  // ── Enable a strategy (add to panel with color) ──
  const enableStrategy = useCallback(async (config: StrategyConfig) => {
    try {
      await strategyApiService.enable(config.id, {
        symbols: config.symbolList,
      });

      const color = getNextColor();
      setActiveStrategies(prev => {
        const next = new Map(prev);
        next.set(config.id, {
          config,
          results: [],
          totalMatches: 0,
          enabled: true,
          color,
        });
        return next;
      });
      setView('results');
    } catch (err) {
      console.error('Failed to enable strategy:', err);
    }
  }, [getNextColor]);

  // ── Disable a strategy ──
  const disableStrategy = useCallback(async (strategyId: string) => {
    try {
      await strategyApiService.disable(strategyId);
      setActiveStrategies(prev => {
        const next = new Map(prev);
        next.delete(strategyId);
        return next;
      });
    } catch (err) {
      console.error('Failed to disable strategy:', err);
    }
  }, []);

  // ── Refresh results ──
  const refreshResults = useCallback(async (strategyId: string) => {
    try {
      const entries = await strategyApiService.getResults(strategyId);
      setActiveStrategies(prev => {
        const next = new Map(prev);
        const existing = next.get(strategyId);
        if (existing) {
          next.set(strategyId, {
            ...existing,
            results: entries,
            totalMatches: entries.length,
          });
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to refresh results:', err);
    }
  }, []);

  // ── Builder save handler — saves to DB & enables ──
  const handleBuilderSave = useCallback(
    async (config: StrategyConfig) => {
      const saved = await saveStrategy(config);
      enableStrategy(saved);
    },
    [saveStrategy, enableStrategy]
  );

  // ── Merge all active strategy results into color-coded rows ──
  const coloredRows = useMemo<ColoredRow[]>(() => {
    const rows: ColoredRow[] = [];
    for (const [id, active] of activeStrategies) {
      for (const result of active.results) {
        rows.push({
          result,
          strategyId: id,
          strategyName: active.config.name,
          color: active.color,
        });
      }
    }
    // Sort by most recent first
    rows.sort(
      (a, b) =>
        new Date(b.result.lastUpdate).getTime() -
        new Date(a.result.lastUpdate).getTime()
    );
    return rows;
  }, [activeStrategies]);

  const getCategoryMeta = (cat: string) =>
    STRATEGY_CATEGORIES.find(c => c.value === cat) ?? {
      value: cat,
      label: cat,
      color: 'bg-gray-600',
      icon: '📋',
    };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            Strategy Builder
          </h2>

          {/* View toggles */}
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                view === 'browse'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => setView('browse')}
            >
              Presets
            </button>
            <button
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                view === 'saved'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => { setView('saved'); loadSavedStrategies(); }}
            >
              Saved ({savedStrategies.length})
            </button>
            <button
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                view === 'results'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => setView('results')}
            >
              Active ({activeStrategies.size})
            </button>
          </div>
        </div>

        <Button
          variant="default"
          size="sm"
          className="h-8"
          onClick={() => {
            setEditingStrategy(null);
            setBuilderOpen(true);
          }}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          New Strategy
        </Button>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-hidden flex">
        {/* ── Left sidebar ── */}
        <div className="w-[380px] border-r border-border overflow-y-auto">
          {/* -- Browse presets -- */}
          {view === 'browse' && (
            <div className="p-3">
              <StrategyPresetBrowser
                onSelect={config => {
                  setSelectedStrategy(config);
                  setView('detail');
                }}
                onEnable={enableStrategy}
                selectedId={selectedStrategy?.id}
              />
            </div>
          )}

          {/* -- Saved strategies -- */}
          {view === 'saved' && (
            <div className="divide-y divide-border">
              {loadingSaved ? (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : savedStrategies.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Save className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No saved strategies</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Create a custom strategy to save it here
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setEditingStrategy(null);
                      setBuilderOpen(true);
                    }}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    New Strategy
                  </Button>
                </div>
              ) : (
                savedStrategies.map(config => {
                  const meta = getCategoryMeta(config.category);
                  const isActive = activeStrategies.has(config.id);
                  const activeColor = activeStrategies.get(config.id)?.color;
                  return (
                    <div
                      key={config.id}
                      className="px-3 py-2.5 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {isActive && activeColor ? (
                            <span
                              className="w-3 h-3 rounded-full shrink-0 ring-1 ring-white/20"
                              style={{ backgroundColor: activeColor }}
                            />
                          ) : (
                            <span className="text-base">{meta.icon}</span>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {config.name}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Badge variant="outline" className="text-[9px] h-4 px-1">
                                {config.category}
                              </Badge>
                              <Badge variant="outline" className="text-[9px] h-4 px-1">
                                {config.alerts.length} alerts
                              </Badge>
                              <Badge variant="outline" className="text-[9px] h-4 px-1">
                                {config.filters.length} filters
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          {isActive ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-400 hover:text-red-600"
                              title="Disable"
                              onClick={() => disableStrategy(config.id)}
                            >
                              <Square className="h-3 w-3" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-green-400 hover:text-green-600"
                              title="Enable"
                              onClick={() => enableStrategy(config)}
                            >
                              <Play className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title="Edit"
                            onClick={() => {
                              setEditingStrategy(config);
                              setBuilderOpen(true);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-red-400 hover:text-red-600"
                            title="Delete"
                            onClick={() => {
                              if (confirm(`Delete "${config.name}"?`))
                                deleteSavedStrategy(config.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* -- Active strategies -- */}
          {view === 'results' && (
            <div className="divide-y divide-border">
              {activeStrategies.size === 0 ? (
                <div className="text-center py-12 px-4">
                  <Zap className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No active strategies</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    Enable a preset or saved strategy to see results
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setView('browse')}
                  >
                    Browse Presets
                  </Button>
                </div>
              ) : (
                Array.from(activeStrategies.entries()).map(([id, active]) => (
                  <div
                    key={id}
                    className="px-3 py-2.5 hover:bg-muted/30 transition-colors border-l-2"
                    style={{ borderLeftColor: active.color }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-3 h-3 rounded-full shrink-0 ring-1 ring-white/20"
                          style={{ backgroundColor: active.color }}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {active.config.name}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge variant="outline" className="text-[9px] h-4 px-1">
                              {active.config.alerts.length} alerts
                            </Badge>
                            <Badge variant="outline" className="text-[9px] h-4 px-1">
                              {active.config.filters.length} filters
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">
                              {active.totalMatches} matches
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          title="Refresh"
                          onClick={e => {
                            e.stopPropagation();
                            refreshResults(id);
                          }}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-400 hover:text-red-600"
                          title="Disable"
                          onClick={e => {
                            e.stopPropagation();
                            disableStrategy(id);
                          }}
                        >
                          <Square className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* -- Detail view -- */}
          {view === 'detail' && selectedStrategy && (
            <StrategyDetailPanel
              strategy={selectedStrategy}
              isSaved={savedStrategies.some(s => s.id === selectedStrategy.id)}
              isActive={activeStrategies.has(selectedStrategy.id)}
              activeColor={activeStrategies.get(selectedStrategy.id)?.color}
              onBack={() => setView('browse')}
              onEnable={() => enableStrategy(selectedStrategy)}
              onSave={() => saveStrategy(selectedStrategy)}
              onEdit={() => {
                setEditingStrategy(selectedStrategy);
                setBuilderOpen(true);
              }}
            />
          )}
        </div>

        {/* ── Right: Color-coded results table (merged from all active strategies) ── */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {activeStrategies.size > 0 ? (
            <>
              {/* Legend bar showing active strategy colors */}
              <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-card/50 flex-wrap">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Strategies:
                </span>
                {Array.from(activeStrategies.entries()).map(([id, active]) => (
                  <div key={id} className="flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full ring-1 ring-white/20"
                      style={{ backgroundColor: active.color }}
                    />
                    <span className="text-[11px] font-medium">
                      {active.config.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      ({active.totalMatches})
                    </span>
                  </div>
                ))}
              </div>

              {/* Results table */}
              <div className="flex-1 overflow-auto">
                {coloredRows.length === 0 ? (
                  <div className="text-center py-16">
                    <Zap className="h-10 w-10 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-sm text-muted-foreground">
                      Waiting for data...
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      Results will appear when alerts fire and filters pass
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-card z-10">
                      <tr className="border-b border-border">
                        <th className="w-[6px] px-0" />
                        <th className="text-left px-2 py-2 font-semibold text-muted-foreground w-[65px]">
                          Time
                        </th>
                        <th className="text-left px-2 py-2 font-semibold text-muted-foreground w-[75px]">
                          Symbol
                        </th>
                        <th className="text-right px-2 py-2 font-semibold text-muted-foreground w-[70px]">
                          Price ($)
                        </th>
                        <th className="text-right px-2 py-2 font-semibold text-muted-foreground w-[80px]">
                          Chg Close (%)
                        </th>
                        <th className="text-right px-2 py-2 font-semibold text-muted-foreground w-[75px]">
                          Vol Today
                        </th>
                        <th className="text-right px-2 py-2 font-semibold text-muted-foreground w-[55px]">
                          Rel Vol
                        </th>
                        <th className="text-right px-2 py-2 font-semibold text-muted-foreground w-[65px]">
                          Flt (Shr)
                        </th>
                        <th className="text-left px-2 py-2 font-semibold text-muted-foreground">
                          Strategy
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {coloredRows.map((row, i) => (
                        <tr
                          key={`${row.strategyId}-${row.result.symbol}-${i}`}
                          className="border-b border-border/30 hover:bg-muted/20 transition-colors"
                        >
                          {/* Color stripe */}
                          <td className="w-[6px] px-0 py-0">
                            <div
                              className="w-[4px] h-full min-h-[28px] rounded-r-sm"
                              style={{ backgroundColor: row.color }}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground">
                            {fmtTime(row.result.lastUpdate)}
                          </td>
                          <td className="px-2 py-1.5 font-bold text-foreground">
                            {row.result.symbol}
                          </td>
                          <td className="text-right px-2 py-1.5 font-mono">
                            ${fmtPrice(row.result.price)}
                          </td>
                          <td
                            className={`text-right px-2 py-1.5 font-mono font-semibold ${chgColor(
                              row.result.changePercent
                            )}`}
                          >
                            {fmtPct(row.result.changePercent)}
                          </td>
                          <td className="text-right px-2 py-1.5 font-mono">
                            {fmtVol(row.result.volume)}
                          </td>
                          <td className="text-right px-2 py-1.5 font-mono">
                            {row.result.relativeVolume.toFixed(1)}x
                          </td>
                          <td className="text-right px-2 py-1.5 font-mono text-muted-foreground">
                            {row.result.float > 0 ? fmtVol(row.result.float) : '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5"
                              style={{
                                backgroundColor: `${row.color}20`,
                                color: row.color,
                              }}
                            >
                              {row.strategyName}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Zap className="h-12 w-12 mx-auto mb-4 text-muted-foreground/15" />
                <p className="text-sm text-muted-foreground">
                  Select or enable a strategy to see results
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Builder dialog ── */}
      <StrategyBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        onSave={handleBuilderSave}
        editStrategy={editingStrategy}
      />
    </div>
  );
};

// ── Detail panel for a selected preset ─────────────────────

const StrategyDetailPanel: React.FC<{
  strategy: StrategyConfig;
  isSaved: boolean;
  isActive: boolean;
  activeColor?: string;
  onBack: () => void;
  onEnable: () => void;
  onSave: () => void;
  onEdit: () => void;
}> = ({ strategy, isSaved, isActive, activeColor, onBack, onEnable, onSave, onEdit }) => {
  const meta = STRATEGY_CATEGORIES.find(c => c.value === strategy.category);

  return (
    <div className="p-3 space-y-4">
      {/* Back */}
      <button
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        onClick={onBack}
      >
        <ChevronRight className="h-3 w-3 rotate-180" />
        Back to presets
      </button>

      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          {isActive && activeColor ? (
            <span
              className="w-4 h-4 rounded-full ring-1 ring-white/20"
              style={{ backgroundColor: activeColor }}
            />
          ) : (
            <span className="text-lg">{meta?.icon ?? '📋'}</span>
          )}
          <h3 className="text-base font-bold">{strategy.name}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[10px]">
            {strategy.category}
          </Badge>
          {isSaved && (
            <Badge variant="secondary" className="text-[10px]">
              <Check className="h-2.5 w-2.5 mr-0.5" />
              Saved
            </Badge>
          )}
          {isActive && (
            <Badge className="text-[10px] bg-green-600">
              Active
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">{strategy.description}</p>
      </div>

      {/* Alerts */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Alerts ({strategy.alerts.length}) — OR logic
        </label>
        <div className="flex flex-wrap gap-1">
          {strategy.alerts.map(code => (
            <Badge key={code} variant="default" className="text-[10px]">
              <Zap className="h-2.5 w-2.5 mr-0.5" />
              {code}
            </Badge>
          ))}
          {strategy.alerts.length === 0 && (
            <span className="text-xs text-muted-foreground/60 italic">
              No alerts (filter-only strategy)
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Filters ({strategy.filters.length}) — AND logic
        </label>
        {strategy.filters.length === 0 ? (
          <span className="text-xs text-muted-foreground/60 italic block">No filters</span>
        ) : (
          <div className="space-y-1">
            {strategy.filters.map((f, i) => {
              const fieldMeta = STRATEGY_FILTER_FIELDS.find(ff => ff.field === f.field);
              const opMeta = FILTER_OPERATORS.find(op => op.value === f.operator);
              return (
                <div
                  key={i}
                  className="flex items-center gap-1.5 text-xs bg-muted/30 rounded px-2 py-1"
                >
                  <Filter className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="font-medium">{fieldMeta?.label ?? f.field}</span>
                  <span className="text-muted-foreground">{opMeta?.symbol ?? f.operator}</span>
                  <span className="font-mono">{f.value}</span>
                  {f.operator === 'Between' && f.value2 != null && (
                    <>
                      <span className="text-muted-foreground">to</span>
                      <span className="font-mono">{f.value2}</span>
                    </>
                  )}
                  {fieldMeta?.unit && (
                    <span className="text-muted-foreground/60">{fieldMeta.unit}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        {isActive ? (
          <Button variant="destructive" size="sm" className="flex-1" disabled>
            <Square className="h-3.5 w-3.5 mr-1" />
            Running
          </Button>
        ) : (
          <Button variant="default" size="sm" className="flex-1" onClick={onEnable}>
            <Play className="h-3.5 w-3.5 mr-1" />
            Enable
          </Button>
        )}
        {!isSaved && !strategy.isPreset && (
          <Button variant="outline" size="sm" onClick={onSave}>
            <Save className="h-3.5 w-3.5 mr-1" />
            Save
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5 mr-1" />
          Customize
        </Button>
      </div>
    </div>
  );
};

export default StrategyDashboard;
